// ============================================
// FILE: src/components/CategoryBannerCarousel.jsx
// ============================================
//
// The home-page hero carousel, driven by the `banners` table and scoped to
// whichever category the shopper has selected.
//
// It replaces a hardcoded fruit-themed array in data/products.js that ignored
// the banners table entirely — so the admin's banner CRUD had no effect on the
// storefront, and a fashion shopper was shown mango promotions.
//
// Selecting a category swaps the creative to that category's banners followed
// by the store's global ones (see Banner::scopeForCategory). Slides rotate on a
// timer that restarts whenever the shopper takes manual control, so a dot tap
// is not immediately overridden by the next tick.

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { banners as fallbackBanners } from '../data/products';
import { resolveProductImageUrl } from '../utils/imageHelpers';
import apiService from '../apiService';

const ROTATE_MS = 5000;

// Banners carry no colour of their own, so the backdrop is assigned by slot.
// This keeps consecutive slides visually distinct without asking an admin to
// pick a gradient, and without a schema column that would only ever hold six
// values.
const GRADIENTS = [
  'linear-gradient(135deg, #ff3f6c 0%, #c2185b 100%)',
  'linear-gradient(135deg, #6366f1 0%, #4338ca 100%)',
  'linear-gradient(135deg, #03a685 0%, #027a63 100%)',
  'linear-gradient(135deg, #d97706 0%, #ea580c 100%)',
  'linear-gradient(135deg, #0ea5e9 0%, #0369a1 100%)',
  'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)',
];

// Both the API rows and the legacy static array are flattened to one shape, so
// the render below never has to branch on where a banner came from.
const normalise = (row, index) => {
  const isStatic = !!(row.bgStyle || row.images);

  return {
    id: row.id ?? index,
    categoryId: row.category_id ?? null,
    title: row.title || '',
    // The DB splits what the static array called subtitle/offer across
    // description and tagline. Tagline is the short one, so it becomes the chip.
    offer: isStatic ? row.offer : row.tagline,
    subtitle: isStatic ? row.subtitle : row.description,
    buttonText: row.button_text || 'Shop Now →',
    linkUrl: row.link_url || null,
    image: isStatic ? null : resolveProductImageUrl(row.image_url),
    images: isStatic ? row.images : null,
    icon: row.icon || '🛍️',
    background: isStatic ? row.bgStyle : GRADIENTS[index % GRADIENTS.length],
  };
};

const CategoryBannerCarousel = ({
  categories = [],
  selectedCategory = 'All',
  onCategoryClick,
  setSelectedCategory,
  isDesktop = false,
  // With no category selected the API returns every category's banner, which
  // on this catalogue is 14+ — a full cycle would take over a minute and the
  // last slides would never be seen. Cap it; the admin's `order` field decides
  // which ones make the cut.
  maxSlides = 6,
  // Pre-fetched banner rows. When given, the carousel shows exactly these —
  // no fetch and no global/static fallback — which is what a per-category
  // shelf needs: that category's own banners, or nothing.
  banners = null,
  // Shelf banners sit inside a category block, so they are shorter and
  // tighter than the page hero.
  compact = false,
}) => {
  const provided = Array.isArray(banners);
  const [slides, setSlides] = useState(() => (provided ? banners.slice(0, maxSlides).map(normalise) : []));
  const [index, setIndex] = useState(0);
  const [loading, setLoading] = useState(!provided);
  const timerRef = useRef(null);

  // The home screen tracks the selection by NAME (its chip strip is built from
  // category names), while the API scopes by id — so resolve one to the other.
  const activeCategory = categories.find((c) => c.name === selectedCategory) || null;
  const activeCategoryId = activeCategory?.id ?? null;

  useEffect(() => {
    if (!provided) return;
    setSlides(banners.slice(0, maxSlides).map(normalise));
    setLoading(false);
  }, [provided, banners, maxSlides]);

  useEffect(() => {
    if (provided) return undefined;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        // Only banners the admin marked "Home Carousel" — a curated set,
        // separate from the ones pinned to the category shelves below.
        const resp = await apiService.getBanners({
          position: 'home_carousel',
          categoryId: activeCategoryId,
        });
        if (cancelled) return;

        // This endpoint returns a bare array, not the {data:{...}} envelope the
        // product endpoints use.
        const rows = Array.isArray(resp?.data) ? resp.data : (Array.isArray(resp) ? resp : []);
        setSlides(rows.slice(0, maxSlides).map(normalise));
      } catch (e) {
        // A failed banner fetch should never take the home page down with it.
        if (!cancelled) setSlides([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [provided, activeCategoryId, maxSlides]);

  // A store with no banners configured still gets a hero rather than a gap.
  // Provided banners never fall back: an empty shelf banner shows nothing.
  const active = slides.length > 0 || provided
    ? slides
    : (loading ? [] : fallbackBanners.map(normalise));

  const minHeight = compact ? (isDesktop ? 160 : 130) : (isDesktop ? 220 : 180);
  const spacing = compact ? 'mb-3' : 'mt-4 mb-6';

  // Changing category swaps the whole set, so an index carried over from the
  // previous one could point past the end.
  useEffect(() => { setIndex(0); }, [activeCategoryId]);

  const startTimer = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (active.length <= 1) return;
    timerRef.current = setInterval(() => {
      setIndex((prev) => (prev + 1) % active.length);
    }, ROTATE_MS);
  }, [active.length]);

  useEffect(() => {
    startTimer();
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [startTimer]);

  // Restart the countdown on a manual jump, so the slide the shopper chose gets
  // a full interval instead of the remainder of the current one.
  const goTo = (i) => {
    setIndex(i);
    startTimer();
  };

  const handleClick = (slide) => {
    if (slide.linkUrl) {
      window.open(slide.linkUrl, '_blank', 'noopener,noreferrer');
      return;
    }

    // A category banner opens its category; a global one just drops the shopper
    // at the product list.
    if (slide.categoryId) {
      const full = categories.find((c) => String(c.id) === String(slide.categoryId));
      if (full && onCategoryClick) {
        onCategoryClick(full);
        return;
      }
      if (full && setSelectedCategory) setSelectedCategory(full.name);
    }

    setTimeout(
      () => document.querySelector('.products-section')?.scrollIntoView({ behavior: 'smooth' }),
      50
    );
  };

  if (loading && slides.length === 0) {
    return (
      <div className={`${isDesktop ? '' : 'px-4'} ${spacing}`}>
        <div
          className="rounded shadow-lg bg-gray-100 animate-pulse"
          style={{ minHeight }}
        />
      </div>
    );
  }

  if (active.length === 0) return null;

  return (
    <div className={`${isDesktop ? '' : 'px-4'} ${spacing} ${compact ? '' : 'banner-carousel'}`}>
      <div className="relative overflow-hidden rounded shadow-lg">
        <div
          className="flex banner-slide"
          style={{
            transform: `translateX(-${index * 100}%)`,
            transition: 'transform 0.5s ease-in-out',
          }}
        >
          {active.map((slide) => (
            <div
              key={slide.id}
              className="min-w-full relative overflow-hidden flex items-center"
              style={{ background: slide.background, minHeight }}
            >
              <div className="absolute -top-10 -right-10 w-44 h-44 rounded-full bg-white/10 pointer-events-none" />
              <div className="absolute -bottom-8 -left-8 w-32 h-32 rounded-full bg-white/10 pointer-events-none" />

              <div className="flex-1 px-5 py-5 z-10" style={{ maxWidth: '55%' }}>
                {slide.offer && (
                  <span className="bg-white/25 backdrop-blur-sm text-white text-[10px] font-bold px-3 py-1 rounded-full inline-block mb-2 tracking-wide uppercase">
                    {slide.offer}
                  </span>
                )}
                <h2
                  className={`font-extrabold text-white leading-tight mb-1 ${isDesktop ? 'text-3xl' : 'text-xl'}`}
                >
                  {slide.title}
                </h2>
                {slide.subtitle && (
                  <p className="text-white/75 text-xs mb-4 leading-relaxed line-clamp-2">
                    {slide.subtitle}
                  </p>
                )}
                <button
                  onClick={() => handleClick(slide)}
                  className="bg-white text-xs font-bold px-4 py-2 rounded shadow-lg hover:scale-105 active:scale-95 transition-transform"
                  style={{ color: '#ff3f6c' }}
                >
                  {slide.buttonText}
                </button>
              </div>

              <div className="absolute right-0 top-0 bottom-0 z-10" style={{ width: '48%' }}>
                {slide.image ? (
                  <img
                    src={slide.image}
                    alt={slide.title}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover"
                    style={{
                      // Fades the photo into the gradient so any image works as
                      // a backdrop without a matching art-directed crop.
                      maskImage: 'linear-gradient(to right, transparent 0%, #000 35%)',
                      WebkitMaskImage: 'linear-gradient(to right, transparent 0%, #000 35%)',
                    }}
                  />
                ) : slide.images ? (
                  // Legacy static banners scatter five thumbnails instead of
                  // using one hero image.
                  slide.images.slice(0, 5).map((src, i) => {
                    const layout = [
                      { width: 72, height: 72, right: 8, top: 6, rotate: 10 },
                      { width: 64, height: 64, right: 52, top: 18, rotate: -6 },
                      { width: 60, height: 60, right: 14, bottom: 10, rotate: 8 },
                      { width: 46, height: 46, right: 60, bottom: 8, rotate: -12 },
                      { width: 40, height: 40, right: 2, top: '42%', rotate: -4 },
                    ][i];
                    return (
                      <img
                        key={i}
                        src={`${process.env.PUBLIC_URL}${src}`}
                        alt=""
                        className="absolute object-contain"
                        style={{
                          ...layout,
                          transform: `rotate(${layout.rotate}deg)`,
                          filter: 'drop-shadow(0 6px 10px rgba(0,0,0,0.3))',
                        }}
                      />
                    );
                  })
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <span
                      style={{
                        fontSize: 90,
                        lineHeight: 1,
                        filter: 'drop-shadow(0 8px 16px rgba(0,0,0,0.25))',
                      }}
                    >
                      {slide.icon}
                    </span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {active.length > 1 && (
          <div className="absolute bottom-3 left-1/2 transform -translate-x-1/2 flex gap-2">
            {active.map((_, i) => (
              <button
                key={i}
                onClick={() => goTo(i)}
                aria-label={`Go to slide ${i + 1}`}
                className={`h-2 rounded-full transition-all ${index === i ? 'bg-white w-6' : 'bg-white/50 w-2'}`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CategoryBannerCarousel;
